import React, { useState, useEffect, useMemo } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { buildMuiTheme } from './theme';
import './App.css'; // Global styles
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import CapturesPage from './pages/CapturesPage';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MainApp from './pages/MainApp';
import ErrorBoundary from './components/ErrorBoundary';
import AdminPanel from './pages/AdminPanel';
import ConfigPage from './pages/ConfigPage';
import KnowledgePage from './pages/KnowledgePage';
import TrafficControlPage from './pages/TrafficControlPage';
import PrivacyHubPage from './pages/PrivacyHubPage';
import ChecksPage from './pages/ChecksPage';
import InspectPage from './pages/InspectPage';
import TrendsPage from './pages/TrendsPage';
import HelpPage from './pages/HelpPage';
import PRDPage from './pages/PRDPage';
import HelpDrawer from './components/HelpDrawer';
import { TrafficProvider, useTraffic } from './context/TrafficContext';
import IsolationNotices from './components/IsolationNotices';
import { parseReqParam } from './utils/deepLinks';
import LoginPage from './pages/LoginPage';
import SessionExpiredModal from './components/SessionExpiredModal';
import { pullProfiles } from './utils/profileSync';
import { initAppPacks } from './api/appPacks';
import { loadKnowledge } from './knowledge';
import { PROXY_WS_DEFAULT, proxyWsLabel } from './utils/proxyWs';

/* ── ATT extraction — reads tracking status from all 6 ad request types ───
   GAM:           validData.attmas  (0-3, parsed by proxy from ?attmas=)
   Prebid:        issues[].code==='att' note, e.g. "ATT=3 (Authorized)"
   IMA:           URL ?attmas= param or validData.checks is_lat
   Amazon A2S:    requestBodyParsed.device.ext.atts (OpenRTB)
   Amazon APS:    URL ?att= or requestBody proprietary field
   SpringServe:   URL ?att= param
──────────────────────────────────────────────────────────────────────────── */
const ATT_LABEL = { '0': 'Not Determined', '1': 'Restricted', '2': 'Denied', '3': 'Authorized' };
const ATT_COLOR = { '0': '#6b7280', '1': '#d97706', '2': '#dc2626', '3': '#16a34a' };

function extractATTAcrossVendors(rows) {
    const seen = {}; // source → attVal (string '0'-'3')

    const trySet = (source, val) => {
        const s = String(val ?? '').trim();
        if (s >= '0' && s <= '3') seen[source] = s;
    };

    for (const r of rows) {
        const rt = r.requestType || r.type || '';
        const url = r.url || '';

        // GAM display / video
        if (rt === 'google' || rt === 'gam_video') {
            if (r.validData?.attmas != null) trySet('GAM', r.validData.attmas);
            else { try { trySet('GAM', new URL(url).searchParams.get('attmas')); } catch {} }
        }

        // Prebid — proxy already parses ATT into issues[]
        if (rt === 'prebid') {
            const attIssue = (r.issues || []).find(i => i.code === 'att');
            if (attIssue) {
                const m = (attIssue.note || '').match(/ATT[=\s]+(\d)/);
                if (m) trySet('Prebid', m[1]);
            }
        }

        // IMA — attmas in URL (same param as GAM) or is_lat in checks
        if (rt === 'ima') {
            try {
                const v = new URL(url).searchParams.get('attmas');
                if (v != null) trySet('IMA', v);
            } catch {}
            // is_lat=0 → tracking allowed (≈ Authorized), is_lat=1 → restricted
            const isLatCheck = (r.validData?.checks || []).find(c => c.id === 'is_lat');
            if (!seen['IMA'] && isLatCheck) {
                try {
                    const lv = new URL(url).searchParams.get('is_lat');
                    if (lv === '0') trySet('IMA', '3'); // not limited → authorized
                    if (lv === '1') trySet('IMA', '2'); // limited → denied
                } catch {}
            }
        }

        // Amazon A2S (video OTT — OpenRTB format)
        if (rt === 'amazon_video') {
            const body = r.requestBodyParsed || (() => { try { return JSON.parse(r.requestBody); } catch { return null; } })();
            const atts = body?.device?.ext?.atts ?? body?.device?.att;
            if (atts != null) trySet('A2S', atts);
        }

        // Amazon APS (display — proprietary format)
        if (rt === 'amazon') {
            try { const v = new URL(url).searchParams.get('att'); if (v != null) trySet('APS', v); } catch {}
            if (!seen['APS']) {
                const body = r.requestBodyParsed || (() => { try { return JSON.parse(r.requestBody); } catch { return null; } })();
                const atts = body?.att ?? body?.device?.ext?.atts ?? body?.device?.att;
                if (atts != null) trySet('APS', atts);
            }
        }

        // SpringServe (VAST URL — ?att= param)
        if (url.includes('springserve.com') || rt === 'springserve') {
            try { const v = new URL(url).searchParams.get('att'); if (v != null) trySet('SpringServe', v); } catch {}
        }
    }

    return seen; // { GAM: '3', Prebid: '3', IMA: '3', ... }
}

/* ── Global proxy status ribbon ────────────────────────────────────────────
   Shown on every tab. Reads ws/proxy/device state pushed into TrafficContext
   by MainApp.js so it stays accurate regardless of which page is active.   */
function ProxyRibbon() {
  const { wsStatus, proxyWsUrl, connectedDevices, liveTrafficGroups, egressStatus } = useTraffic();
  const deviceEntries = Object.entries(connectedDevices || {});
  const totalRequests = deviceEntries.reduce((s, [, n]) => s + n, 0);

  // Connection lost banner. Renders IN FLOW, directly under the sticky
  // .global-nav — never position:fixed. A fixed banner at top:0 outranks the
  // nav's z-index and covers the Admin/Logout menu, so a disconnected proxy
  // locked you out of the very controls you reach for when it disconnects.
  if (wsStatus !== 'connected') {
    return (
      <div style={{
        flexShrink: 0,
        background: wsStatus === 'connecting' ? '#1e3a5f' : '#7c2d12',
        color: '#fff', padding: '10px 20px',
        display: 'flex', alignItems: 'center', gap: 12,
        fontSize: 13, fontWeight: 500,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      }}>
        <svg width="16" height="16" viewBox="0 0 16 16" style={{ flexShrink: 0, animation: 'ws-spin 1s linear infinite' }}>
          <circle cx="8" cy="8" r="6" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2.5" />
          <path d="M8 2 A6 6 0 0 1 14 8" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
          <style>{`@keyframes ws-spin { to { transform: rotate(360deg); } }`}</style>
        </svg>
        <span>
          {wsStatus === 'connecting'
            ? 'Connecting to validation server…'
            : 'Connection lost — reconnecting… Your captured data is safe.'}
        </span>
        <span style={{ marginLeft: 'auto', opacity: 0.6, fontSize: 11 }}>
          {proxyWsUrl || PROXY_WS_DEFAULT}
        </span>
      </div>
    );
  }

  // Connected state — compact inline pill bar (doesn't obscure content)
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '4px 16px', fontSize: 11,
      background: 'var(--surface)',
      borderBottom: '1px solid var(--line)',
      flexWrap: 'wrap',
    }}>
      <span style={{ color: '#22c55e', fontWeight: 600 }}>● Proxy</span>
      <span style={{ color: 'var(--muted)', fontSize: 10, fontFamily: 'monospace' }}>
        {proxyWsLabel(proxyWsUrl)}
      </span>
      {deviceEntries.length > 0 && (
        <>
          <span style={{ color: 'var(--line)' }}>·</span>
          {deviceEntries.map(([key, count]) => (
            <span key={key} style={{
              padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 600,
              background: 'var(--primary)', color: '#fff',
            }}>
              {key} ({count})
            </span>
          ))}
          <span style={{ color: 'var(--muted)' }}>{totalRequests} requests</span>
        </>
      )}

      {/* Datacenter-egress warning — the proxy's own egress IP is a hosting/VPN
          IP, so SSPs/DSPs will score ALL proxied traffic as invalid (nbr=5). */}
      {egressStatus?.datacenter && (
        <>
          <span style={{ color: 'var(--line)' }}>·</span>
          <span
            title={`Egress IP ${egressStatus.ip || 'unknown'} (${egressStatus.org || egressStatus.isp || 'unknown org'})\nSSPs/DSPs will classify this as datacenter/hosting traffic (OpenRTB nbr=5).\nRead bids and no-bids on proxied traffic as test-quality, not production.`}
            style={{
              padding: '1px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
              background: '#d9770622', border: '1px solid #d9770666', color: '#d97706',
              cursor: 'default', whiteSpace: 'nowrap',
            }}
          >
            ⚠ Datacenter egress — SSPs may flag traffic as IVT
          </span>
        </>
      )}

      {/* ATT badge — reads tracking status from all 6 ad vendor request types */}
      {(() => {
        const allRows = Object.values(liveTrafficGroups || {}).flat();
        if (allRows.length === 0) return null;
        const attMap = extractATTAcrossVendors(allRows);
        const entries = Object.entries(attMap); // [source, val]
        if (entries.length === 0) return null;

        // Check consistency — all vendors should agree
        const vals = [...new Set(entries.map(([, v]) => v))];
        const isConsistent = vals.length === 1;
        const displayVal = vals[0]; // most common or only value
        const label = ATT_LABEL[displayVal] || displayVal;
        const color = isConsistent ? ATT_COLOR[displayVal] : '#d97706';

        const tooltip = entries.map(([src, v]) => `${src}: ${ATT_LABEL[v] || v}`).join(' · ');

        return (
          <>
            <span style={{ color: 'var(--line)' }}>·</span>
            <span
              title={tooltip}
              style={{
                padding: '1px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                background: `${color}22`, border: `1px solid ${color}66`, color,
                cursor: 'default', whiteSpace: 'nowrap',
              }}
            >
              {isConsistent ? '' : '⚠ '}ATT: {label}
              {!isConsistent && ` (${vals.length} values)`}
            </span>
          </>
        );
      })()}
    </div>
  );
}

// IU Finder moved into Checks (?mode=iu); old links keep their ?q= / ?iu=.
function IuFinderRedirect() {
  const location = useLocation();
  const sp = new URLSearchParams(location.search);
  sp.set('mode', 'iu');
  return <Navigate to={`/checks?${sp.toString()}`} replace />;
}

// Secondary QA-tool pages, grouped under the nav's "Tools" dropdown so the
// primary bar (Dashboard/Admin) stays uncluttered as more tools get added.
const NAV_TOOLS = [
  { to: '/traffic', label: 'Traffic control', icon: SwapHorizOutlinedIcon },
  { to: '/privacy', label: 'Privacy', icon: ShieldOutlinedIcon },
  { to: '/inspect', label: 'Inspect', icon: TravelExploreOutlinedIcon },
  { to: '/trends', label: 'Trends', icon: TrendingUpOutlinedIcon },
];

// Setup/reference pages grouped into the "Admin" dropdown alongside Admin Panel —
// app-config and knowledge-base curation, not active per-session QA tools.
const ADMIN_LINKS = [
  { to: '/admin', label: 'Admin Panel', icon: AdminPanelSettingsOutlinedIcon },
  { to: '/config', label: 'Config', icon: SettingsOutlinedIcon },
  { to: '/knowledge', label: 'Knowledge', icon: MenuBookOutlinedIcon },
  // A writing aid, not a validator — kept out of Tools so that menu is only QA work.
  { to: '/prd', label: 'PRD Builder', icon: DescriptionOutlinedIcon },
];

// Shared MUI MenuItem styling for the dark "capture console" dropdowns
// (Tools + Admin/Account) — active/hover states match the .nav-link CSS.
function navMenuItemSx(active) {
  return {
    fontSize: 13,
    color: active ? '#ffffff' : '#93a8af',
    '&:hover': { background: 'rgba(79,179,188,0.14)', color: '#ffffff' },
    '&.Mui-selected': { background: 'rgba(79,179,188,0.18)', color: '#ffffff' },
    '&.Mui-selected:hover': { background: 'rgba(79,179,188,0.22)' },
  };
}
const NAV_MENU_PAPER_SX = {
  mt: 0.5,
  minWidth: 240,
  background: '#0d1b24',
  border: '1px solid #234049',
  borderRadius: '10px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
};

// Rendered inside <Router> (as a child of MainLayout's <Router>) so
// useLocation() has router context for active-tab highlighting.
function GlobalNav() {
  const location = useLocation();
  const [toolsAnchor, setToolsAnchor] = useState(null);
  const isToolsActive = NAV_TOOLS.some(t => location.pathname === t.to);


  return (
    <div className="nav-links">
      <Link to="/" className="nav-brand" style={{ textDecoration: 'none' }}>
        <div className="nav-brand-dot" />
        AdLens FX
      </Link>
      <div className="nav-divider" />
      <Link to="/" className={`nav-link${location.pathname === '/' ? ' active' : ''}`}>Dashboard</Link>
      {/* Saved HARs — the core save/reopen loop, so a primary link, not a Tools entry */}
      <Link to="/captures" className={`nav-link${location.pathname === '/captures' ? ' active' : ''}`}>Captures</Link>
      {/* Checks is the only sign-off / validate surface — primary bar, not buried in Tools */}
      <Link to="/checks" className={`nav-link${location.pathname === '/checks' ? ' active' : ''}`}>Checks</Link>
      <button
        type="button"
        className={`nav-link nav-tools-btn${isToolsActive ? ' active' : ''}`}
        onClick={(e) => setToolsAnchor(e.currentTarget)}
        aria-haspopup="true"
        aria-expanded={Boolean(toolsAnchor)}
      >
        Tools
        <KeyboardArrowDownIcon sx={{ fontSize: 16, ml: '2px' }} />
      </button>
      <Menu
        anchorEl={toolsAnchor}
        open={Boolean(toolsAnchor)}
        onClose={() => setToolsAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: NAV_MENU_PAPER_SX } }}
      >
        {NAV_TOOLS.map(({ to, label, icon: Icon }) => {
          const active = location.pathname === to;
          return (
            <MenuItem
              key={to}
              component={Link}
              to={to}
              onClick={() => setToolsAnchor(null)}
              selected={active}
              sx={navMenuItemSx(active)}
            >
              <ListItemIcon sx={{ minWidth: 30, color: 'inherit' }}>
                <Icon sx={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText primary={label} primaryTypographyProps={{ fontSize: 13 }} />
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
}

// Top-right "Admin ▾" dropdown — groups the Admin Panel link with account-level
// actions (theme, logout) that don't belong among the primary nav tabs.
// Rendered inside <Router> (as a child of MainLayout's <Router>) so
// useLocation() has router context for active-tab highlighting.
function AccountMenu({ theme, setTheme, sessionId }) {
  const location = useLocation();
  const [anchor, setAnchor] = useState(null);
  const isAdminActive = ADMIN_LINKS.some(l => location.pathname === l.to);

  // This used to prompt "End current session?" and then only remove debug_ssid
  // before reloading — it never cleared the credentials, so Logout left you
  // signed in (the reload just re-read a still-valid token). Every other auth
  // path clears both keys: notifyAuthExpired() on a 401, and LoginPage on entry.
  //
  // The wording mattered too: "End current session?" reads like the CAPTURE
  // session, which is the New session button's job, so people reached for Logout
  // expecting a clean slate and got neither. Captured traffic is deliberately NOT
  // touched here — authFetch's notifyAuthExpired says why: a token expiring
  // mid-run must not destroy the capture. Clearing traffic is New session's job.
  const handleLogout = () => {
    setAnchor(null);
    if (window.confirm('Log out? Captured traffic is kept — use "New session" to clear it.')) {
      try {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('debug_ssid');
      } catch { /* private browsing / storage disabled — reload still signs out in practice */ }
      window.location.reload();
    }
  };

  return (
    <>
      <button
        type="button"
        className={`nav-link nav-tools-btn${isAdminActive ? ' active' : ''}`}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="true"
        aria-expanded={Boolean(anchor)}
      >
        <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 16, mr: '4px' }} />
        Admin
        <KeyboardArrowDownIcon sx={{ fontSize: 16, ml: '2px' }} />
      </button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { ...NAV_MENU_PAPER_SX, minWidth: 220 } } }}
      >
        {ADMIN_LINKS.map(({ to, label, icon: Icon }) => {
          const active = location.pathname === to;
          return (
            <MenuItem
              key={to}
              component={Link}
              to={to}
              onClick={() => setAnchor(null)}
              selected={active}
              sx={navMenuItemSx(active)}
            >
              <ListItemIcon sx={{ minWidth: 30, color: 'inherit' }}>
                <Icon sx={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText primary={label} primaryTypographyProps={{ fontSize: 13 }} />
            </MenuItem>
          );
        })}

        <Divider sx={{ borderColor: '#234049', my: 0.5 }} />

        <MenuItem onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')} sx={navMenuItemSx(false)}>
          <ListItemIcon sx={{ minWidth: 30, color: 'inherit' }}>
            {theme === 'dark'
              ? <DarkModeOutlinedIcon sx={{ fontSize: 18 }} />
              : <LightModeOutlinedIcon sx={{ fontSize: 18 }} />}
          </ListItemIcon>
          <ListItemText
            primary={theme === 'dark' ? 'Dark theme' : 'Light theme'}
            secondary="Click to switch"
            primaryTypographyProps={{ fontSize: 13 }}
            secondaryTypographyProps={{ fontSize: 11, color: '#5f7d85' }}
          />
        </MenuItem>

        <MenuItem onClick={handleLogout} sx={{ ...navMenuItemSx(false), whiteSpace: 'normal' }}>
          <ListItemIcon sx={{ minWidth: 30, color: 'inherit' }}>
            <LogoutOutlinedIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText primary={`Logout · ${sessionId.slice(0, 6)}`} primaryTypographyProps={{ fontSize: 13 }} />
        </MenuItem>
      </Menu>
    </>
  );
}

// The Dashboard (MainApp) owns the live WebSocket + captured traffic. Keep it
// MOUNTED across all routes (shown only on "/") so navigating to another tab
// never tears down the socket or drops the capture — it keeps capturing in the
// background and the full session survives the round trip. Rendered inside
// <Router> so useLocation() has router context.
function RoutedContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setJumpTarget } = useTraffic();
  const onDashboard = location.pathname === '/';

  // Deep-link entry point (WP1, docs/launch-signoff-spec.md §4): a shared
  // `/?req=<requestId>` URL (see utils/deepLinks.js) is handed off to
  // TrafficContext's `jumpTarget` channel — already consumed by
  // InspectraNetworkView's jump effect — then the param is stripped via a
  // replace-navigate so a later re-render (e.g. jumpTarget resolving once
  // the row lands, or any other state update) doesn't re-trigger the jump.
  // Only acts when the param is actually present, to avoid a strip/no-op
  // loop on every render.
  useEffect(() => {
    const reqId = parseReqParam(location.search);
    if (!reqId) return;
    setJumpTarget(reqId);
    navigate('/', { replace: true, state: location.state });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Breadcrumb back — every in-app jump (chat row-ref clicks, evidence-row
  // clicks, etc.) navigates with `state: { from, fromLabel }`. Only shown on
  // the dashboard itself (nothing to "come back to" from elsewhere) and only
  // when that state survived the trip.
  const backTarget = onDashboard ? location.state?.from : null;
  const backLabel = location.state?.fromLabel || backTarget;

  return (
    <div className="app-content">
      <div style={{ display: onDashboard ? 'contents' : 'none' }}>
        <ErrorBoundary label="Dashboard">
          <MainApp onDashboard={onDashboard} />
        </ErrorBoundary>
      </div>
      {backTarget && (
        <button
          type="button"
          onClick={() => navigate(backTarget)}
          style={{
            position: 'fixed',
            left: 16,
            bottom: 16,
            zIndex: 9998,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 20,
            border: '1px solid var(--line)',
            background: 'var(--surface)',
            color: 'var(--text)',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
          }}
        >
          ← Back to {backLabel}
        </button>
      )}
      <ErrorBoundary label="page">
      <Routes>
        <Route path="/" element={null} />
        <Route path="/ask" element={<Navigate to="/checks" replace />} />
        <Route path="/chat" element={<Navigate to="/checks" replace />} />
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/captures" element={<CapturesPage />} />
        <Route path="/config" element={<ConfigPage />} />
        <Route path="/traffic" element={<TrafficControlPage />} />
        {/* IU Rewrite and Chaos are tabs of Traffic control now — keep old links working. */}
        <Route path="/rewrite" element={<Navigate to="/traffic?tab=rewrite" replace />} />
        <Route path="/privacy" element={<PrivacyHubPage />} />
        {/* Disclosures, Consent, IDs and app-ads.txt are tabs of Privacy now — keep old links working. */}
        <Route path="/disclosures" element={<Navigate to="/privacy?tab=disclosures" replace />} />
        <Route path="/checks" element={<ChecksPage />} />
        {/* Launch and Rules are modes of Checks now — keep old links working. */}
        <Route path="/launch" element={<Navigate to="/checks?mode=signoff" replace />} />
        {/* Releases + Inbox (Release Copilot) retired 2026-10-08 — Checks → Sign off is the one sign-off system. */}
        <Route path="/inbox" element={<Navigate to="/checks?mode=signoff" replace />} />
        <Route path="/releases" element={<Navigate to="/checks?mode=signoff" replace />} />
        <Route path="/knowledge" element={<KnowledgePage />} />
        <Route path="/inspect" element={<InspectPage />} />
        <Route path="/iu-finder" element={<IuFinderRedirect />} />
        {/* GAM Explainer, ORTB Lint, VAST and Diff are tabs of Inspect now — keep old links working. */}
        <Route path="/diff" element={<Navigate to="/inspect?tab=diff" replace />} />
        <Route path="/consent" element={<Navigate to="/privacy?tab=consent" replace />} />
        <Route path="/vast" element={<Navigate to="/inspect?tab=vast" replace />} />
        <Route path="/explain" element={<Navigate to="/inspect?tab=gam" replace />} />
        <Route path="/ortb-lint" element={<Navigate to="/inspect?tab=ortb" replace />} />
        <Route path="/ids" element={<Navigate to="/privacy?tab=ids" replace />} />
        <Route path="/chaos" element={<Navigate to="/traffic?tab=chaos" replace />} />
        <Route path="/appads" element={<Navigate to="/privacy?tab=appads" replace />} />
        <Route path="/rules" element={<Navigate to="/checks?mode=rules" replace />} />
        <Route path="/trends" element={<TrendsPage />} />
        <Route path="/prd" element={<PRDPage />} />
        <Route path="/help" element={<HelpPage />} />
      </Routes>
      </ErrorBoundary>
    </div>
  );
}

function MainLayout() {
  const { sessionId } = useTraffic();

  // ── Pull profiles from DB on mount — no auth needed for profile config ─────
  useEffect(() => { pullProfiles(); }, []);

  // ── Fetch App Packs from the proxy on mount — brand/app data (taxonomy,
  // prebidMappings, content-API rules, etc.) now lives there. Sync accessors
  // (getPacks/getPack/findPackByIu) return seed-JSON data immediately and
  // upgrade in place once this resolves — no loading state needed anywhere.
  useEffect(() => { initAppPacks(); }, []);

  // ── Fetch the vendor knowledge packs from the proxy on mount — the proxy
  // (proxy-server/knowledge/*.json) is now the single source of truth; this
  // populates the in-memory cache in src/knowledge/index.js before the user
  // reaches a validation pass or the Knowledge page in normal use. Idempotent
  // + fire-and-forget (see src/knowledge/index.js for the small race window
  // this leaves for a validation that runs before the fetch resolves).
  useEffect(() => { loadKnowledge(); }, []);

  // ---- Global Theme State (lifted from MainApp) ----
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  useEffect(() => {
    document.body.dataset.theme = theme;
    localStorage.setItem('theme', theme);
  }, [theme]);
  // MUI components (Paper, TextField, Table, ...) don't pick up the app's
  // CSS-variable theme on their own — this feeds them the same variables so
  // e.g. ChecksPage.jsx cards match the rest of the dark/light theme.
  const muiTheme = useMemo(() => buildMuiTheme(theme === 'dark' ? 'dark' : 'light'), [theme]);

  if (!sessionId) {
    return (
      <ThemeProvider theme={muiTheme}>
        <LoginPage />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <Router>
        <div className="app-shell">
          {/* In-app re-auth overlay for an expired backend token — renders over
              the still-mounted dashboard so the live WS + captured session
              survive a re-login (see SessionExpiredModal). */}
          <SessionExpiredModal />
          <nav className="global-nav">
            {/* Left: Brand + Links */}
            <GlobalNav />

            {/* Right: per-page help ("?"), then Admin + account actions (theme,
                logout) grouped in one dropdown. Help sits outside the Admin menu
                on purpose — it answers "what is this screen", which is asked
                while looking at the screen, not while hunting through a menu. */}
            <div className="nav-actions">
              <HelpDrawer />
              <AccountMenu theme={theme} setTheme={setTheme} sessionId={sessionId} />
            </div>
          </nav>

          {/* Global proxy/WS status ribbon — visible on all tabs */}
          <ProxyRibbon />
          <IsolationNotices />

          <RoutedContent />

          <div className="session-footer">
            Session · <b>{sessionId}</b>
          </div>
        </div>
      </Router>
    </ThemeProvider>
  );
}

function App() {
  return (
    <TrafficProvider>
      <MainLayout />
    </TrafficProvider>
  );
}

export default App;
